import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import generalRequest from "../../request/generalRequest";
import { getInitialUserData } from "../../context/allStates/userData";
import { Eye, EyeOff } from "lucide-react";
import PasswordField from "../common/passwordField/passwordField";
import "./signinPage.css"

const SignInPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { userData, setUserData, language } = useContext(CombinedContext);
    const navigate = useNavigate();

    const handleLogin = async () => {
        setError("");
        setLoading(true);

        try {
            const data = await usersRequest.login({ email, password });

            const formattedData = {
                ...data,
                cognitoTokens: {
                    IdToken: data.cognitoTokens?.IdToken || data.IdToken,
                    AccessToken: data.cognitoTokens?.AccessToken || data.AccessToken,
                    RefreshToken: data.cognitoTokens?.RefreshToken || data.RefreshToken,
                },
            };

            setUserData(formattedData);

            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens.RefreshToken) {
                storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
            }
            storage.setItem("user", JSON.stringify(formattedData.user));
            if (formattedData.shop) {
                storage.setItem("shop", JSON.stringify(formattedData.shop));
            }

            if (
                formattedData.user?.role === "admin" ||
                formattedData.user?.role === "owner" ||
                formattedData.user?.role === "staff"
            ) {
                return navigate("/calendar");
            }

            navigate("/");
        } catch (err) {
            console.error("Login failed:", err);
            setError(err.message || "Invalid email or password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sf-signin-page">
            <div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
                <div className="hd">
                    <div>{translations[language].signIn.title}</div>
                </div>
                <div className="bd">
                    <input
                        className="input"
                        placeholder={translations[language].signIn.email}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <PasswordField
                        value={password}
                        onChange={setPassword}
                        placeholder={translations[language].signIn.password}
                        autoComplete="current-password"
                    />

                    <div style={{ marginBottom: 10, display: "flex", alignItems: "center", marginTop: 10, }}>
                        <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            style={{ marginRight: 6 }}
                        />
                        <label htmlFor="rememberMe">{translations[language].signIn.rememberMe}</label>
                    </div>

                    <button
                        className="btn brand"
                        style={{ width: "100%", marginBottom: 10 }}
                        onClick={handleLogin}
                        disabled={loading}
                    >
                        {loading ? translations[language].signIn.loading : translations[language].signIn.button}
                    </button>

                    {error && (
                        <div className="sub" style={{ color: "red", marginBottom: 10 }}>
                            {error}
                        </div>
                    )}

                    <div className="sub" style={{ marginBottom: 6 }}>
                        <Link to="/forgot-password">{translations[language].signIn.forgotPassword}</Link>
                    </div>

                    <div className="sub" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div>{translations[language].signIn.noAccount}</div>

                    <Link to="/register">{translations[language].signIn.createCustomer}</Link>

                    <Link to="/listyourshop">{translations[language].signIn.registerBusiness}</Link>
                </div>
                </div>
            </div>
        </div>
    );
};

export default SignInPage;
